import { Flex } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import {
  AnonymousHitterAvatar,
  CommonHitterAvatar,
} from 'components/common/hitters/avatar';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { CommonHitterSessionStats } from 'components/common/hitters/session-stats';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { SessionHitsTable } from 'components/sections/analytics/session-hits/table';
import { SessionHitsToolbar } from 'components/sections/analytics/session-hits/toolbar';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { HittersContext } from 'contexts/hitters.context';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { IOptionExt } from 'interfaces/forms/option-ext';
import {
  ANONYMOUS_HITTER,
  ANONYMOUS_HITTER_ID,
} from 'lib_ts/enums/hitters.enums';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import { orderBy } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

interface IProps {
  backToPitches: () => void;
}

const getHitterID = (hitter?: IHitter): string => {
  // we can pass in an empty string to the table for query purposes, but not to the select input in the toolbar
  if (hitter?._id === ANONYMOUS_HITTER_ID) {
    return '';
  }

  return hitter?._id ?? '';
};

export const PitchListHittingResults = (props: IProps) => {
  const { machine } = useContext(MachineContext);
  const { isMetric } = machine;

  // ensure the session machine is synced with the machine context since this flow doesn't trigger fetchHitsAndMachine
  useEffect(() => {
    useAnalyticsStore.setState({ sessionMachine: machine });
  }, [machine]);

  const loading = useAnalyticsStore((s) => s.loadingSessions || s.loadingHits);
  const hits = useAnalyticsStore((s) => s.hits);

  const current = useAuthStore((s) => s.current);

  const { active, hitters, stats } = useContext(HittersContext);

  const sessionHitters = useMemo(() => {
    // hitters that have faced a pitch in this session
    const values = hitters.filter((h) => Object.keys(stats).includes(h._id));

    // if the active hitter has not faced a pitch yet - add them to the list
    if (active && !values.find((h) => h._id === active?._id)) {
      values.push(active as IHitter);
    }

    return orderBy(values, ['lastActive'], ['desc']);
  }, [hitters, stats, active]);

  const hitterOptions = useMemo<IOptionExt[]>(() => {
    const sessionHitterOptions: IOptionExt[] = sessionHitters.map((h) => ({
      label: h.name,
      value: h._id,
      suffix: <HitterSideBadge value={h.side} />,
      prefix: <CommonHitterAvatar size="1" hitter={h} clickDisabled />,
    }));

    return [
      {
        label: t('hitters.anonymous'),
        value: ANONYMOUS_HITTER_ID,
        prefix: <AnonymousHitterAvatar size="1" />,
      },
      ...sessionHitterOptions,
    ];
  }, [sessionHitters]);

  const [selectedHitter, setSelectedHitter] = useState<IHitter | undefined>(
    active || ANONYMOUS_HITTER
  );

  const sessionID = current.session;
  const hitterID = getHitterID(selectedHitter);

  return (
    <ErrorBoundary identifier="PitchListHittingResults">
      <Flex height="100%" direction="column">
        <FlexTableWrapper
          gap="1"
          header={
            <Flex direction="column" gap="3">
              <SessionHitsToolbar
                hitterOptions={hitterOptions}
                selectedHitter={selectedHitter}
                setSelectedHitterId={(value) => {
                  if (value === ANONYMOUS_HITTER_ID) {
                    setSelectedHitter(ANONYMOUS_HITTER);
                    return;
                  }

                  setSelectedHitter(
                    sessionHitters.find((h) => h._id === value) as IHitter
                  );
                }}
              />
              <CommonHitterSessionStats
                loading={loading}
                isMetric={!!isMetric}
                hits={hits}
              />
            </Flex>
          }
          table={
            <SessionHitsTable
              identifier="AnalyticsHitterSessionTable"
              sessionID={sessionID}
              hitterID={hitterID}
              backToPitches={props.backToPitches}
              machineID={current.machineID}
              skipHeader
            />
          }
        />
      </Flex>
    </ErrorBoundary>
  );
};
